import { useState, useEffect } from 'react';
import { Card, Table, Button, Tag, Space, Modal, Upload, message, Select, Typography, Descriptions, Alert, Empty, Progress, Statistic, Row, Col, Tooltip, Form, Input, Divider, CopyOutlined, Timeline, Badge } from 'antd';
import { UploadOutlined, FileTextOutlined, DownloadOutlined, DeleteOutlined, EyeOutlined, StopOutlined, ReloadOutlined, InboxOutlined, CheckCircleOutlined, ClockCircleOutlined, ExclamationCircleOutlined, SyncOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { batchAPI } from '../services/api';
import { t } from '../utils/i18n';

const { Title, Text, Paragraph } = Typography;
const { Dragger } = Upload;

const Batches = () => {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detailsVisible, setDetailsVisible] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [currentBatch, setCurrentBatch] = useState(null);
  const [stats, setStats] = useState({ total: 0, pending: 0, in_progress: 0, completed: 0, failed: 0, cancelled: 0 });
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadedFileId, setUploadedFileId] = useState(null);
  const [form] = Form.useForm();
  const [helpVisible, setHelpVisible] = useState(false);

  useEffect(() => {
    fetchBatches();
    // 每10秒自动刷新批处理列表，实现进度条实时更新
    const interval = setInterval(() => {
      fetchBatches();
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  const fetchBatches = async () => {
    setLoading(true);
    try {
      const response = await batchAPI.list();
      const listData = response.data || response;
      if (listData) {
        const batchList = Array.isArray(listData) ? listData : listData.batches || listData.data || [];
        setBatches(batchList);
        updateStats(batchList);
      }
    } catch (error) {
      console.error('Failed to fetch batches:', error);
      message.error(t('fetch_failed'));
    } finally {
      setLoading(false);
    }
  };

  const updateStats = (batchList) => {
    const newStats = {
      total: batchList.length,
      pending: batchList.filter(b => b.status === 'pending').length,
      in_progress: batchList.filter(b => b.status === 'in_progress').length,
      completed: batchList.filter(b => b.status === 'completed').length,
      failed: batchList.filter(b => b.status === 'failed').length,
      cancelled: batchList.filter(b => b.status === 'cancelled').length,
    };
    setStats(newStats);
  };

  const handleCreateBatch = () => {
    setCreateModalVisible(true);
    setUploadedFile(null);
    setUploadedFileId(null);
    form.resetFields();
  };

  // 处理文件上传
  const handleFileUpload = async (file) => {
    const isJsonl = file.name.endsWith('.jsonl');
    if (!isJsonl) {
      message.error(t('only_jsonl_allowed'));
      return false;
    }

    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) {
      message.error(t('file_too_large'));
      return false;
    }

    setUploadLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('purpose', 'batch');

      const response = await batchAPI.uploadFile(formData);

      const fileData = response.data || response;
      if (fileData.id) {
        setUploadedFile(file);
        setUploadedFileId(fileData.id);
        message.success(t('upload_success'));
      } else {
        message.error(t('upload_failed'));
      }
    } catch (error) {
      console.error('Upload error:', error);
      message.error(t('upload_failed') || '文件上传失败');
    } finally {
      setUploadLoading(false);
    }
    return false;
  };

  // 创建批处理任务
  const handleCreateSubmit = async (values) => {
    if (!uploadedFileId) {
      message.error(t('please_upload_file'));
      return;
    }

    try {
      await batchAPI.create({
        name: values.name || `批量任务_${new Date().toLocaleString('zh-CN').replace(/[/:]/g, '').replace(/\s/g, '_')}`,
        endpoint: values.endpoint || '/v1/chat/completions',
        input_file_id: uploadedFileId,
        completion_window: values.completion_window || '24h',
      });
      message.success(t('batch_created'));
      setCreateModalVisible(false);
      setUploadedFile(null);
      setUploadedFileId(null);
      form.resetFields();
      fetchBatches();
    } catch (error) {
      console.error('Create batch error:', error);
      message.error(t('create_failed'));
    }
  };

  const showDetails = async (batch) => {
    try {
      const response = await batchAPI.get(batch.id);
      // 兼容 response.data 和直接返回的情况
      const batchData = response.data || response;
      setCurrentBatch(batchData);
      setDetailsVisible(true);
    } catch (error) {
      console.error('Failed to fetch batch detail:', error);
      message.error(t('fetch_failed'));
    }
  };

  const handleCancel = async (batch) => {
    Modal.confirm({
      title: t('confirm_cancel_batch'),
      content: t('cancel_batch_warning'),
      onOk: async () => {
        try {
          await batchAPI.cancel(batch.id);
          message.success(t('batch_cancelled'));
          fetchBatches();
        } catch (error) {
          message.error(t('cancel_failed'));
        }
      },
    });
  };

  const handleDelete = async (batch) => {
    Modal.confirm({
      title: t('confirm_delete') || '确认删除',
      content: t('delete_batch_warning') || '删除后任务及其输入输出文件将无法恢复，确认删除？',
      okType: 'danger',
      onOk: async () => {
        try {
          await batchAPI.delete(batch.id);
          message.success(t('delete_success') || '删除成功');
          setDetailsVisible(false);
          fetchBatches();
        } catch (error) {
          message.error(t('delete_failed') || '删除失败');
        }
      },
    });
  };

  const downloadResult = async (batch) => {
    try {
      const response = await batchAPI.getResult(batch.id);
      const resultData = response.data || response;
      if (resultData) {
        const blob = new Blob([typeof resultData === 'string' ? resultData : JSON.stringify(resultData, null, 2)], { type: 'application/jsonl' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `batch_result_${batch.id}.jsonl`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Download result error:', error);
      message.error(t('download') + ' ' + t('failed'));
    }
  };

  const getStatusTag = (status) => {
    const statusMap = {
      'pending': { color: 'gold', text: t('pending'), icon: <ClockCircleOutlined /> },
      'in_progress': { color: 'blue', text: t('processing'), icon: <SyncOutlined spin /> },
      'completed': { color: 'green', text: t('completed'), icon: <CheckCircleOutlined /> },
      'failed': { color: 'red', text: t('failed'), icon: <ExclamationCircleOutlined /> },
      'cancelled': { color: 'default', text: t('cancelled'), icon: <DeleteOutlined /> },
    };
    const config = statusMap[status] || { color: 'default', text: status, icon: null };
    return <Tag color={config.color} icon={config.icon}>{config.text}</Tag>;
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '-';
    
    let date;
    if (typeof timestamp === 'string') {
      date = new Date(timestamp);
    } else if (typeof timestamp === 'number') {
      if (timestamp < 1e11) {
        date = new Date(timestamp * 1000);
      } else {
        date = new Date(timestamp);
      }
    } else {
      return '-';
    }
    
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const formatEndpoint = (endpoint) => {
    if (!endpoint) return '-';
    return endpoint.replace('/v1/', '');
  };

  // 计算进度
  const getProgress = (batch) => {
    if (!batch.total_count || batch.total_count === 0) return 0;
    return Math.round(((batch.success_count || 0) + (batch.failed_count || 0)) / batch.total_count * 100);
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      render: (id) => (
        <Text type="secondary" style={{ fontFamily: 'monospace', fontSize: 12 }}>
          {id}
        </Text>
      ),
    },
    {
      title: t('batch_name'),
      dataIndex: 'name',
      key: 'name',
      width: 200,
      ellipsis: true,
      render: (name) => (
        <Text strong style={{ fontSize: 13 }}>{name || t('batch_task_name_auto')}</Text>
      ),
    },
    {
      title: t('status'),
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (status) => getStatusTag(status),
    },
    {
      title: t('progress'),
      key: 'progress',
      width: 160,
      render: (_, record) => {
        const progress = getProgress(record);
        const successRate = record.total_count > 0 
          ? ((record.success_count || 0) / record.total_count * 100).toFixed(1) 
          : '0';
        return (
          <div>
            <Progress 
              percent={progress} 
              size="small" 
              status={record.status === 'failed' ? 'exception' : record.status === 'completed' ? 'success' : 'active'}
              format={(p) => `${p}%`}
              style={{ marginBottom: 4 }}
            />
            <Text type="secondary" style={{ fontSize: 11 }}>
              {t('batch_success_count')} {record.success_count || 0} / {t('batch_failed_count')} {record.failed_count || 0} ({successRate}%)
            </Text>
          </div>
        );
      },
    },
    {
      title: t('model'),
      dataIndex: 'model',
      key: 'model',
      width: 130,
      render: (model) => model ? <Tag color="blue">{model}</Tag> : <Text type="secondary">-</Text>,
    },
    {
      title: t('created_at'),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (time) => formatTime(time),
    },
    {
      title: t('actions'),
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space>
          <Tooltip title={t('detail')}>
            <Button size="small" icon={<EyeOutlined />} onClick={() => showDetails(record)} />
          </Tooltip>
          {(record.status === 'pending' || record.status === 'in_progress') && (
            <Tooltip title={t('cancel')}>
              <Button size="small" danger icon={<StopOutlined />} onClick={() => handleCancel(record)} />
            </Tooltip>
          )}
          {(record.status === 'completed' || record.status === 'failed' || record.status === 'cancelled') && (
            <Tooltip title={t('delete')}>
              <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record)} />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 24 }}>
        <Title level={4}>{t('batch_processing')}</Title>
        <Text type="secondary">{t('batch_processing_desc')}</Text>
      </div>

      {/* 统计卡片 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={4}>
          <Card size="small">
            <Statistic title={t('total')} value={stats.total} />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small">
            <Statistic title={t('pending')} value={stats.pending} valueStyle={{ color: '#faad14' }} />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small">
            <Statistic title={t('processing')} value={stats.in_progress} valueStyle={{ color: '#1890ff' }} />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small">
            <Statistic title={t('completed')} value={stats.completed} valueStyle={{ color: '#52c41a' }} />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small">
            <Statistic title={t('failed')} value={stats.failed} valueStyle={{ color: '#ff4d4f' }} />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small">
            <Statistic title={t('cancelled')} value={stats.cancelled} />
          </Card>
        </Col>
      </Row>

      {/* 提示信息 */}
      <Alert
        message={t('batch_tip_title')}
        description={
          <div>
            <p>{t('batch_tip_1')}</p>
            <p>{t('batch_tip_2')}</p>
            <p>{t('batch_tip_4')}</p>
          </div>
        }
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      {/* 操作栏 */}
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <Button type="primary" icon={<UploadOutlined />} onClick={handleCreateBatch}>
          {t('create_batch')}
        </Button>
        <Button icon={<ReloadOutlined />} onClick={fetchBatches}>
          {t('refresh')}
        </Button>
      </div>

      {/* 批量任务列表 */}
      <Card>
        {batches.length === 0 ? (
          <Empty description={t('no_batches')} image={Empty.PRESENTED_IMAGE_SIMPLE}>
            <Button type="primary" onClick={handleCreateBatch}>
              {t('create_first_batch')}
            </Button>
          </Empty>
        ) : (
          <Table
            columns={columns}
            dataSource={batches}
            rowKey="id"
            loading={loading}
            pagination={{ pageSize: 10 }}
          />
        )}
      </Card>

      {/* 详情弹窗 - 重新设计 */}
      <Modal
        title={
          <Space>
            <FileTextOutlined />
            <span>{t('batch_detail')}</span>
            {currentBatch && getStatusTag(currentBatch.status)}
          </Space>
        }
        open={detailsVisible}
        onCancel={() => setDetailsVisible(false)}
        footer={
          currentBatch && (currentBatch.status === 'completed' || currentBatch.status === 'failed' || currentBatch.status === 'cancelled') ? (
            <Button danger icon={<DeleteOutlined />} onClick={() => handleDelete(currentBatch)}>
              {t('delete') || '删除'}
            </Button>
          ) : null
        }
        width={700}
        destroyOnHidden
      >
        {currentBatch && (
          <div>
            {/* 进度概览 */}
            {currentBatch.total_count > 0 && (
              <Card size="small" style={{ marginBottom: 16, background: '#fafafa' }}>
                <Row gutter={16}>
                  <Col span={6}>
                    <Statistic 
                      title={t('batch_total_tasks')} 
                      value={currentBatch.total_count || 0} 
                      valueStyle={{ fontSize: 20 }}
                    />
                  </Col>
                  <Col span={6}>
                    <Statistic 
                      title={t('batch_success_count')} 
                      value={currentBatch.success_count || 0} 
                      valueStyle={{ fontSize: 20, color: '#52c41a' }}
                    />
                  </Col>
                  <Col span={6}>
                    <Statistic 
                      title={t('batch_failed_count')} 
                      value={currentBatch.failed_count || 0} 
                      valueStyle={{ fontSize: 20, color: '#ff4d4f' }}
                    />
                  </Col>
                  <Col span={6}>
                    <Statistic 
                      title={t('batch_completion_rate')} 
                      value={`${getProgress(currentBatch)}%`} 
                      valueStyle={{ fontSize: 20, color: '#1890ff' }}
                    />
                  </Col>
                </Row>
                <Progress 
                  percent={getProgress(currentBatch)} 
                  status={currentBatch.status === 'failed' ? 'exception' : currentBatch.status === 'completed' ? 'success' : 'active'}
                  style={{ marginTop: 16 }}
                />
              </Card>
            )}

            {/* 基本信息 */}
            <Descriptions column={2} bordered size="small">
              <Descriptions.Item label={t('batch_task_id')} span={2}>
                <Text copyable style={{ fontFamily: 'monospace', fontSize: 11 }}>
                  {currentBatch.id}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label={t('batch_type')}>
                <Tag color="blue">{formatEndpoint(currentBatch.endpoint)}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('model')}>
                {currentBatch.model || '-'}
              </Descriptions.Item>
              <Descriptions.Item label={t('input_file')}>
                <Space>
                  <FileTextOutlined />
                  <Text>{currentBatch.file_name || currentBatch.input_file_id || currentBatch.inputs || '-'}</Text>
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label={t('output_file')}>
                {currentBatch.output_file_id ? (
                  <Space direction="vertical" size={4}>
                    <Button size="small" type="primary" icon={<DownloadOutlined />} onClick={() => downloadResult(currentBatch)}>
                      {t('download')}
                    </Button>
                    <Text type="warning" style={{ fontSize: 11 }}>
                      {t('batch_result_expiry_notice') || '结果文件将在完成后 7 天自动删除，请及时下载'}
                    </Text>
                  </Space>
                ) : (
                  <Text type="secondary">-</Text>
                )}
              </Descriptions.Item>
              <Descriptions.Item label={t('created_at')}>
                {formatTime(currentBatch.created_at)}
              </Descriptions.Item>
              <Descriptions.Item label={t('completed_at')}>
                {formatTime(currentBatch.completed_at)}
              </Descriptions.Item>
            </Descriptions>

            {/* 时间线 */}
            <Divider orientation="left">{t('batch_timeline')}</Divider>
            <Timeline
              items={[
                {
                  color: 'green',
                  children: (
                    <div>
                      <Text strong>{t('batch_task_created')}</Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {formatTime(currentBatch.created_at)}
                      </Text>
                    </div>
                  ),
                },
                ...(currentBatch.in_progress_at ? [{
                  color: 'blue',
                  children: (
                    <div>
                      <Text strong>{t('batch_task_started')}</Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {formatTime(currentBatch.in_progress_at)}
                      </Text>
                    </div>
                  ),
                }] : []),
                ...(currentBatch.status === 'completed' || currentBatch.status === 'failed' ? [{
                  color: currentBatch.status === 'completed' ? 'green' : 'red',
                  children: (
                    <div>
                      <Text strong>{currentBatch.status === 'completed' ? t('batch_task_completed') : t('batch_task_failed')}</Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {formatTime(currentBatch.completed_at)}
                      </Text>
                    </div>
                  ),
                }] : []),
              ]}
            />
          </div>
        )}
      </Modal>

      {/* 创建批量任务弹窗 */}
      <Modal
        title={t('create_batch')}
        open={createModalVisible}
        onCancel={() => setCreateModalVisible(false)}
        footer={null}
        width={600}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" onFinish={handleCreateSubmit}>
          {/* 文件上传区域 */}
          <Form.Item label={t('jsonl_file')}>
            {uploadedFile ? (
              <Card size="small" style={{ background: '#f6ffed', borderColor: '#52c41a' }}>
                <Space>
                  <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 20 }} />
                  <Text strong>{uploadedFile.name}</Text>
                  <Button
                    type="link"
                    danger
                    size="small"
                    onClick={() => {
                      setUploadedFile(null);
                      setUploadedFileId(null);
                    }}
                  >
                    {t('remove')}
                  </Button>
                </Space>
                <Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0, fontSize: 12 }}>
                  {(uploadedFile.size / 1024).toFixed(2)} KB
                </Paragraph>
              </Card>
            ) : (
              <Dragger
                name="file"
                multiple={false}
                accept=".jsonl"
                beforeUpload={handleFileUpload}
                showUploadList={false}
                loading={uploadLoading}
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined style={{ fontSize: 48, color: '#1890ff' }} />
                </p>
                <p className="ant-upload-text">{t('click_or_drag_file')}</p>
                <p className="ant-upload-hint">
                  {t('jsonl_format_hint')}
                </p>
              </Dragger>
            )}
          </Form.Item>

          <Form.Item
            name="name"
            label={t('batch_name')}
            tooltip={t('batch_name_auto_tooltip') || 'Leave blank to auto-generate'}
          >
            <Input placeholder={t('batch_name_auto_placeholder') || 'Leave blank to auto-generate'} />
          </Form.Item>

          <Divider />

          <Form.Item
            name="endpoint"
            label={t('endpoint')}
            initialValue="/v1/chat/completions"
          >
            <Select>
              <Select.Option value="/v1/chat/completions">
                <Space>
                  <Tag color="blue">Chat</Tag>
                  {t('chat_completions')}
                </Space>
              </Select.Option>
              <Select.Option value="/v1/embeddings">
                <Space>
                  <Tag color="green">Embed</Tag>
                  {t('embeddings')}
                </Space>
              </Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="completion_window"
            label={t('completion_window')}
            initialValue="24h"
          >
            <Select>
              <Select.Option value="24h">
                <Space>
                  <ClockCircleOutlined />
                  24 {t('hours')}
                </Space>
              </Select.Option>
            </Select>
          </Form.Item>

          <Alert
            message={t('batch_notice')}
            description={
              <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                <li>{t('batch_notice_1')}</li>
                <li>{t('batch_notice_3')}</li>
              </ul>
            }
            type="info"
            showIcon
            style={{ marginBottom: 8 }}
          />

          <Alert
            message={t('batch_privacy_notice') || '我们重视您的数据隐私：所有批处理文件仅存储在加密的临时存储中，不写入任何数据库，结果文件自完成后 7 天自动永久删除'}
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setCreateModalVisible(false)}>
                {t('cancel')}
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                icon={<UploadOutlined />}
                disabled={!uploadedFileId}
              >
                {t('create_batch')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 使用说明 */}
      <Button
        type="text"
        icon={<QuestionCircleOutlined />}
        onClick={() => setHelpVisible(true)}
        style={{ position: 'fixed', bottom: 24, right: 24, opacity: 0.6 }}
      >
        {t('help')}
      </Button>

      <Modal
        title={t('batch_help_title')}
        open={helpVisible}
        onCancel={() => setHelpVisible(false)}
        footer={null}
        width={700}
      >
        <div style={{ maxHeight: 500, overflow: 'auto' }}>
          <Alert message={t('batch_help_quick_start')} type="info" showIcon style={{ marginBottom: 16 }} />
          
          <Title level={5}>{t('batch_help_prepare_file')}</Title>
          <Paragraph>
            {t('batch_help_prepare_file_desc')}
          </Paragraph>
          <Card size="small" style={{ marginBottom: 16, background: '#f5f5f5' }}>
            <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: 12 }}>
{`{"custom_id": "request-1", "method": "POST", "url": "/v1/chat/completions", "body": {"model": "gpt-3.5-turbo", "messages": [{"role": "user", "content": "Hello"}]}}
{"custom_id": "request-2", "method": "POST", "url": "/v1/chat/completions", "body": {"model": "gpt-3.5-turbo", "messages": [{"role": "user", "content": "How is the weather today"}]}}`}
            </pre>
          </Card>

          <Title level={5}>{t('batch_help_param_desc')}</Title>
          <Descriptions bordered size="small" style={{ marginBottom: 16 }}>
            <Descriptions.Item label="custom_id">{t('batch_help_custom_id')}</Descriptions.Item>
            <Descriptions.Item label="method">{t('batch_help_method')}</Descriptions.Item>
            <Descriptions.Item label="url">{t('batch_help_url')}</Descriptions.Item>
            <Descriptions.Item label="body">{t('batch_help_body')}</Descriptions.Item>
          </Descriptions>

          <Title level={5}>{t('batch_help_supported_api')}</Title>
          <Tag color="blue">/v1/chat/completions</Tag>
          <Tag color="blue">/v1/completions</Tag>
          <Tag color="blue">/v1/embeddings</Tag>
          <Tag color="blue">/v1/images/generations</Tag>
          <div style={{ marginTop: 16, marginBottom: 16 }}>
            <Alert message={t('batch_help_api_note')} type="warning" showIcon />
          </div>

          <Title level={5}>{t('batch_help_example_chat')}</Title>
          <Card size="small" style={{ marginBottom: 16, background: '#f5f5f5' }}>
            <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: 12 }}>
{`{"custom_id": "chat-1", "method": "POST", "url": "/v1/chat/completions", "body": {"model": "gpt-4", "messages": [{"role": "system", "content": "You are a helpful assistant"}, {"role": "user", "content": "Explain quantum computing"}]}}`}
            </pre>
          </Card>

          <Title level={5}>{t('batch_help_view_result')}</Title>
          <Paragraph>
            {t('batch_help_view_result_desc')}
          </Paragraph>

          <Title level={5}>{t('batch_help_limit')}</Title>
          <ul>
            <li>{t('batch_help_limit_1')}</li>
            <li>{t('batch_help_limit_2')}</li>
            <li>{t('batch_help_limit_3')}</li>
          </ul>
        </div>
      </Modal>
    </div>
  );
};

export default Batches;
